import React, { useEffect, useState, useRef } from 'react';
import { NylasScheduling } from '@nylas/react';
import { create } from 'apisauce';
import { Loading } from 'components/Loading';
import NotFound from 'pages/NotFound';
import { observeShadowRoots, applyStyles } from 'components/Scheduler/utils/shadowRoot';
import { localSchedulerPreviewRootStyle } from 'components/Scheduler/injected-scheduler-style';

import './scheduler.scss';

const schedulerStyle = {
  '--nylas-primary': '#2b2b2b',
  '--nylas-error': '#cc4841',
  '--nylas-error-pressed': '#992222',
  '--nylas-wraning': '#f06c00',
  '--nylas-success': '#16a392',
  '--nylas-info': '#2b8fc2',
  '--nylas-base-0': '#ffffff',
  '--nylas-base-25': '#fcfcfd',
  '--nylas-base-50': '#f8f9fc',
  '--nylas-base-100': '#eaecf5',
  '--nylas-base-200': '#ebebeb',
  '--nylas-base-300': '#727272', // '#b3b8d8'
  '--nylas-base-400': '#717bbc',
  '--nylas-base-500': '#bea06e',
  '--nylas-base-600': '#3d3d3d',
  '--nylas-base-700': '#3d3d3d', // '#263f72'
  '--nylas-base-800': '#2b2b2b',
  '--nylas-base-900': '#101323',
  '--nylas-base-950': '#0e101b',
  '--nylas-border-radius': '0',
  '--nylas-border-radius-2x': '0',
  '--nylas-border-radius-3x': '0',
  '--nylas-font-family': '"Montserrat", sans-serif',
};

const STYLED_SHADOW_ELEMENTS = [
  'nylas-scheduling',
  'nylas-notification',
  'nylas-selected-event-card',
  'nylas-booking-form',
  'input-component',
  'radio-button-group',
  'checkbox-component',
  'select-dropdown',
  'button-component',
];
const OBSERVABLE_SHADOW_ELEMENTS = [
  'nylas-booking-form',
];

const Scheduler = ({ configurationId, error }) => {
  const [showNylasScheduling, setShowNylasScheduling] = useState(false);
  const [logo, setLogo] = useState();
  const [notFound, setNotFound] = useState(false);
  const [showError, setShowError] = useState(false);
  const componentRef = useRef();

  const getSchedulerUiSettings = async () => {
    const api = create({
      baseURL: 'https://api.us.nylas.com/v3/scheduling',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    try {
      const response = await api.get(`/ui-settings?configuration_id=${configurationId}`);
      if (response.status === 200) {
        setShowNylasScheduling(true);
        const { data } = response.data;
        const { scheduler } = data || {};
        const { email_template } = scheduler || {};
        const { logo: _logo } = email_template || {};
        if (_logo) setLogo(_logo);
      } else if (response.status === 404) {
        setNotFound(true);
      } else {
        setShowError(true);
      }
    } catch {
      setShowError(true);
    }
  };

  useEffect(() => {
    if (configurationId && !error) {
      getSchedulerUiSettings();
    }
  }, [configurationId]);

  const updateLocalShadowRoots = () => {
    if (!componentRef?.current) return;

    observeShadowRoots(componentRef.current, (element) => {
      const tagName = element.tagName.toLowerCase();
      if (STYLED_SHADOW_ELEMENTS.includes(tagName)) {
        applyStyles(element, null, localSchedulerPreviewRootStyle);
      }
    }, OBSERVABLE_SHADOW_ELEMENTS);
  };

  const scheduleShadowUpdates = () => {
    // A small delay so that the DOM has time to update.
    setTimeout(() => {
      updateLocalShadowRoots();
    }, 0);
  };

  if (!configurationId && !error) {
    return (
      <Loading />
    );
  }

  if (notFound || error || showError || configurationId === 0) {
    return <NotFound />;
  }

  return (
    <>
      <div className="scheduler__header">
        {logo && (
          <img
            src={logo}
            className="company-logo"
          />
        )}
      </div>
      {showNylasScheduling && (
        <NylasScheduling
          ref={componentRef}
          configurationId={configurationId}
          enableUserFeedback={false}
          nylasBranding={false}
          themeConfig={schedulerStyle}
          eventOverrides={{
            timeslotConfirmed: async () => {
              scheduleShadowUpdates();
            },
          }}
        />
      )}
    </>
  );
};

export default Scheduler;
